import Button from "@/common/components/Button";
import Modal from "@/common/components/Modal";
import { useEffect, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { maintenanceSpecificationSchema, MaintenanceSpecificationValidationSchema } from "../../../schemas/update.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { EstablishmentLeadData, MaintenanceSpecificationDirectiveKlass } from "../../../types/establishmentLead.type";
import { useCreateEstablishmentLeadMaintenanceSpecification } from "../../../hooks/maintenanceModules/useCreateEstablishmentLeadMaintenanceSpecification";
import { useQueryClient } from "@tanstack/react-query";
import { FormTransformers } from "@/common/utils/formTransformers";
import MaintenanceSpecificationForm from "./MaintenanceSpecificationForm";

interface CreateMaintenanceSpecificationModalProps {
  directiveKlass: MaintenanceSpecificationDirectiveKlass;
  establishmentLead: EstablishmentLeadData;
  alertText: {
    title: string;
    description: string;
  };
  disabled?: boolean;
}

const CreateMaintenanceSpecificationModal = ({
  directiveKlass,
  establishmentLead,
  alertText,
  disabled,
}: CreateMaintenanceSpecificationModalProps) => {
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);

  const formMethods = useForm<MaintenanceSpecificationValidationSchema>({
    resolver: zodResolver(maintenanceSpecificationSchema),
    defaultValues: {
      directive_klass: directiveKlass,
    },
  });

  const {
    setError,
    handleSubmit,
    reset,
  } = formMethods;

  const {
    mutate: createEstablishmentLeadMaintenanceSpecification,
    isPending: createEstablishmentLeadMaintenanceSpecificationIsPending,
  } = useCreateEstablishmentLeadMaintenanceSpecification({
    establishmentLeadId: establishmentLead.id,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['establishmentLead', establishmentLead.id] });

      setOpen(false);
    },
    onError: FormTransformers.errorsTransformer<MaintenanceSpecificationValidationSchema>({
      setError,
    }),
  });

  const onSubmit: SubmitHandler<MaintenanceSpecificationValidationSchema> = (data) => createEstablishmentLeadMaintenanceSpecification(data);

  useEffect(() => {
    if (open) {
      reset({
        directive_klass: directiveKlass,
      });
    }
  }, [open, directiveKlass]);

  return (
    <div onClick={(event) => event.stopPropagation()}>
      <Modal
        isOpen={open}
        setIsOpen={setOpen}
        triggerAsChild
        triggerDisabled={disabled}
        Trigger={(
          <Button
            className="py-2 px-4 text-sm font-normal"
            onClick={(event) => event.stopPropagation()}
            operationKlassToCheckPermission="Admin::Funnel::Establishment::Leads::UpdateMaintenanceModels"
            disabled={disabled}
          >
            Nova especificação
          </Button>
        )}
        title="Especificando atendimentos"
        useCancellableFooter
        confirmButtonClassName="bg-blue-700 rounded-br-lg"
        cancelButtonClassName="bg-white-200 rounded-bl-lg"
        onConfirm={handleSubmit(onSubmit)}
        confirmButtonIsLoading={createEstablishmentLeadMaintenanceSpecificationIsPending}
        body={(
          <FormProvider {...formMethods}>
            <MaintenanceSpecificationForm
              alertText={alertText}
            />
          </FormProvider>
        )}
      />
    </div>
  );
}

export default CreateMaintenanceSpecificationModal;
